import { z } from 'zod';
import { ratingSchema } from './comments.js';
/**
 * Contrats des ACTIONS DE MASSE sur une SÉLECTION d'assets (barre de sélection de la
 * galerie). Source unique de vérité front + back.
 *
 * Ce fichier héberge les contrats de sélection groupée. LOT 3 : la NOTATION en masse
 * (« Noter la sélection », 0..5 étoiles) — une mutation DB SYNCHRONE (comme le
 * `PATCH /api/assets/:id/rating` unitaire), PAS un job. LOT 4 : l'AJOUT DE TAGS
 * EXISTANTS en masse (« Ajouter un tag » — pose des liaisons `asset_tags` validées),
 * aussi une mutation DB SYNCHRONE.
 *
 * ⚠ PIÈGE ZOD v4 (boot crash) : ces schémas de REQUÊTE passent par
 * `z.toJSONSchema(schema, { target: 'draft-7' })` au démarrage (enregistrement des
 * routes Fastify). Un `.transform()` PERSONNALISÉ y LÈVE → l'API plante en boucle. On
 * n'en met AUCUN ici (uniquement `.array`/`.min`/`.uuid` + `ratingSchema` en
 * `.int().min().max()` — représentables). Garde anti-régression :
 * `route-json-schema.test.ts` rejoue `z.toJSONSchema` dessus.
 */
// ---------------------------------------------------------------------------
// Notation de masse (POST /api/libraries/:id/assets/bulk-rating)
// ---------------------------------------------------------------------------
/**
 * Corps de la notation de masse : une sélection NON VIDE d'ids d'assets + la note
 * 0..5 à leur appliquer (`ratingSchema` réutilisé du contrat unitaire ; `0` = « non
 * noté »).
 */
export const bulkRatingRequestSchema = z.object({
    assetIds: z.array(z.uuid()).min(1),
    rating: ratingSchema,
});
/** Résultat : nombre d'assets dont la note a EFFECTIVEMENT été mise à jour. */
export const bulkRatingResultSchema = z.object({
    updated: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Ajout de tags EXISTANTS en masse (POST /api/libraries/:id/assets/bulk-tags)
// ---------------------------------------------------------------------------
/**
 * Corps de l'ajout de tags EXISTANTS en masse (LOT 4) : une sélection NON VIDE
 * d'assets + une sélection NON VIDE de tags DÉJÀ existants (par leur `id`) à leur
 * appliquer. Restreint aux tags gérés de la taxonomie — AUCUNE création en ligne de
 * nouveau tag ici (l'utilisateur ne fournit que des `id` de tags existants). Le
 * backend borne tout à la bibliothèque (les tags/assets d'une autre biblio, ou les
 * assets supprimés, ne peuvent jamais être liés). Pas de `.transform` (représentable
 * en JSON Schema — cf. garde `route-json-schema.test.ts`).
 */
export const bulkTagRequestSchema = z.object({
    assetIds: z.array(z.uuid()).min(1),
    tagIds: z.array(z.uuid()).min(1),
});
/** Résultat : nombre de liaisons (asset, tag) créées ou promues en `validated`. */
export const bulkTagResultSchema = z.object({
    linked: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Retrait de tags en masse (POST /api/libraries/:id/assets/bulk-untags)
// ---------------------------------------------------------------------------
/**
 * Corps du RETRAIT de tags en masse : une sélection NON VIDE d'assets + une sélection
 * NON VIDE de tags (par leur `id`) à DÉTACHER de ces assets. Le backend supprime toute
 * liaison `asset_tags` (quel que soit son statut : `validated`, `proposed`…) pour chaque
 * paire (asset, tag) du produit cartésien, BORNÉE à la bibliothèque (jamais un asset/tag
 * d'une autre biblio). Idempotent : retirer un tag absent de la sélection est un no-op.
 * Pas de `.transform` (représentable en JSON Schema — cf. `route-json-schema.test.ts`).
 */
export const bulkUntagRequestSchema = z.object({
    assetIds: z.array(z.uuid()).min(1),
    tagIds: z.array(z.uuid()).min(1),
});
/** Résultat : nombre de liaisons (asset, tag) EFFECTIVEMENT supprimées. */
export const bulkUntagResultSchema = z.object({
    unlinked: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Tags présents sur une sélection (POST /api/libraries/:id/assets/selection-tags)
// ---------------------------------------------------------------------------
/**
 * Corps de la requête « tags présents sur la sélection » (alimente le mode « Retirer »
 * de la modale de tags) : une sélection NON VIDE d'assets. LECTURE seule (aucune
 * mutation) → accès lecture suffisant. Pas de `.transform`.
 */
export const selectionTagsRequestSchema = z.object({
    assetIds: z.array(z.uuid()).min(1),
});
/**
 * Un tag présent sur la sélection : son `id`, son `label` et `count` = nombre d'assets
 * de la sélection qui le portent (liaison VALIDÉE) — analogue du « utilisé N× » mais
 * borné à la sélection courante.
 */
export const selectionTagSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    count: z.number().int().nonnegative(),
});
/** Résultat : les tags VALIDÉS portés par ≥ 1 asset de la sélection (triés par label). */
export const selectionTagsResultSchema = z.object({
    tags: z.array(selectionTagSchema),
});
// ---------------------------------------------------------------------------
// Acceptation des suggestions IA (POST /api/libraries/:id/assets/accept-ai-tags)
// ---------------------------------------------------------------------------
/**
 * Corps de l'acceptation EN MASSE des suggestions IA (« Accepter les suggestions IA ») :
 * une sélection NON VIDE d'assets. Le backend PROMEUT en `validated` toutes les liaisons
 * de tag `proposed` de ces assets (bornées à la bibliothèque). NE TOUCHE PAS le drapeau
 * `assets.tagged` → les photos restent « À valider (IA) » (source `ai` conservée) tant
 * qu'un humain ne les a pas finalisées. Pas de `.transform` (représentable en JSON
 * Schema — cf. garde `route-json-schema.test.ts`).
 */
export const acceptAiTagsRequestSchema = z.object({
    assetIds: z.array(z.uuid()).min(1),
});
/** Résultat : nombre de liaisons `proposed` promues en `validated`. */
export const acceptAiTagsResultSchema = z.object({
    accepted: z.number().int().nonnegative(),
});
//# sourceMappingURL=bulk-actions.js.map